/**
 * User Events Chat Hook
 *
 * Persists a single superadmin-scoped conversation keyed by the viewer's
 * user id so chat history survives reloads.
 */

import React from 'react';
import { DefaultChatTransport } from 'ai';
import { useToast } from '@chatsdet/ui';
import {
  useChatCore,
  convertPersistedToUIMessage,
  useMessageSync,
  notifyChatFailure,
} from '../../../../../hooks/chat';
import { API_URL } from '../../../../../config';
import { useAuth } from '../../../../../contexts/AuthContext';
import {
  useConversations,
  type ConversationMessage,
} from '../../../../../hooks/useConversations';
import type { UserEventsUIMessage } from '../types';

const createTransport = (conversationId: string) =>
  new DefaultChatTransport<UserEventsUIMessage>({
    api: `${API_URL}/superadmin/user-events/chat/messages`,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Conversation-Id': conversationId,
    },
  });

const metadataMapper = (msg: ConversationMessage): UserEventsUIMessage['metadata'] => ({
  originalRole: msg.role as 'system' | 'user' | 'assistant' | 'tool',
  raw: { createdAt: msg.createdAt },
});

export function useUserEventsChat() {
  const { toast } = useToast();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const hasInitializedRef = React.useRef<string | null>(null);

  const {
    activeConversation,
    messages: persistedMessages,
    isLoadingConversations,
    isLoadingMessages,
    getOrCreateConversation,
    archiveConversation,
    addMessages,
    isCreatingConversation,
  } = useConversations({
    entityType: 'user-events-admin',
    entityId: userId,
    enabled: !!userId,
    singleConversation: true,
  });

  React.useEffect(() => {
    if (!userId || isLoadingConversations || activeConversation || isCreatingConversation) {
      return;
    }
    if (hasInitializedRef.current === userId) return;
    hasInitializedRef.current = userId;

    void getOrCreateConversation('User Events Explorer').catch((err) => {
      console.error('Failed to create user-events conversation', err);
    });
  }, [
    userId,
    isLoadingConversations,
    activeConversation,
    isCreatingConversation,
    getOrCreateConversation,
  ]);

  React.useEffect(() => {
    if (userId !== hasInitializedRef.current) {
      hasInitializedRef.current = null;
    }
  }, [userId]);

  const transport = React.useMemo(
    () => (activeConversation ? createTransport(activeConversation.id) : null),
    [activeConversation]
  );

  const initialMessages = React.useMemo(
    () =>
      persistedMessages.map((msg) =>
        convertPersistedToUIMessage<UserEventsUIMessage>(msg, metadataMapper)
      ),
    [persistedMessages]
  );

  const core = useChatCore<UserEventsUIMessage>({
    chatId: `user-events-chat-${activeConversation?.id ?? 'none'}`,
    transport,
    canSend: () => !!activeConversation,
    beforeSend: async (text) => {
      if (!activeConversation) return;
      const clientMessageId = crypto.randomUUID();
      await addMessages([
        { clientMessageId, role: 'user', content: text, parts: [{ type: 'text', text }] },
      ]);
    },
    // The assistant turn is persisted server-side (decoupled from SSE delivery)
    // so a dropped stream can't lose a fully-generated reply. See
    // apps/api/src/ai/chat/persist-assistant-stream.ts.
    onError: () => notifyChatFailure(toast),
  });

  useMessageSync({
    conversationId: activeConversation?.id ?? null,
    initialMessages,
    setMessages: core.setMessages,
    currentMessageCount: core.messages.length,
  });

  const handleClearChat = React.useCallback(async () => {
    if (activeConversation) {
      try {
        await archiveConversation();
        await getOrCreateConversation('User Events Explorer');
      } catch (error) {
        console.error('Failed to clear chat', error);
        toast.error('Unable to clear chat history');
        return;
      }
    }
    core.clear();
  }, [activeConversation, archiveConversation, getOrCreateConversation, core, toast]);

  return {
    messages: core.messages,
    chatInput: core.input,
    setChatInput: core.setInput,
    isSending: core.isSending,
    isEnabled: !!activeConversation,
    isLoadingConversation: isLoadingConversations || isLoadingMessages,
    handleSendMessage: core.sendMessage,
    handleStopChat: core.stop,
    handleClearChat,
  };
}
